export interface LongPressHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
}

export interface LongPressLifecycle {
  onStart?: (x: number, y: number) => void;
  onCancel?: () => void;
  /** Delay in ms before onStart fires. Lets casual taps pass without triggering the visual. Default 0. */
  startDelay?: number;
}

export function createLongPressHandlers(
  onFire: (x: number, y: number) => void,
  duration = 600,
  cancelDistance = 10,
  lifecycle?: LongPressLifecycle
): LongPressHandlers {
  let startTimer: ReturnType<typeof setTimeout> | null = null;
  let fireTimer: ReturnType<typeof setTimeout> | null = null;
  let startX = 0;
  let startY = 0;
  let hasStarted = false; // true after onStart has fired

  const cancel = () => {
    if (startTimer !== null) {
      clearTimeout(startTimer);
      startTimer = null;
    }
    if (fireTimer !== null) {
      clearTimeout(fireTimer);
      fireTimer = null;
      if (hasStarted) {
        lifecycle?.onCancel?.();
      }
    }
    hasStarted = false;
  };

  return {
    onPointerDown: (e: PointerEvent) => {
      startX = e.clientX;
      startY = e.clientY;
      cancel();
      const delay = lifecycle?.startDelay ?? 0;
      if (delay > 0) {
        startTimer = setTimeout(() => {
          startTimer = null;
          hasStarted = true;
          lifecycle?.onStart?.(e.clientX, e.clientY);
        }, delay);
      } else {
        hasStarted = true;
        lifecycle?.onStart?.(e.clientX, e.clientY);
      }
      fireTimer = setTimeout(() => {
        fireTimer = null;
        hasStarted = false;
        onFire(e.clientX, e.clientY);
      }, duration);
    },
    onPointerMove: (e: PointerEvent) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.sqrt(dx * dx + dy * dy) > cancelDistance) {
        cancel();
      }
    },
    onPointerUp: cancel,
  };
}
